import { useState, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { useTranslation } from 'react-i18next';
import { PartnerEnrollmentStatus } from '../../../common/enums';
import { PartnerEnrollmentStatusBadge } from '../../../common/PartnerEnrollmentStatusBadge';
import { useNavigationUtils } from '../../../common/utils/useNavigationUtils';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { useAppSelector } from '../../../store/hooks';
import { selectMemberSession } from '../../../store/slices/onboardingSlice';
import VideoPlayer from '../../../common/VideoPlayer';
import OptimusButton from '../../../common/library/Button/Button';
import { HDEmailModal } from './HDEmailModal';
import { usePartnerConfigList } from '../Partner/Enrollment/usePartnerConfigList';
import { homeDepotPartnerId } from '../../../common/utils/PartnerEnrollmentUtils';
import {
  HD_SITE_URL,
  HD_SIGN_IN_VIDEO_URL,
  HD_SIGN_UP_VIDEO_URL,
  THE_HOME_DEPOT_CODE,
  THE_HOME_DEPOT_NAME,
  getPartnerShopButtonBrandStyle,
} from '../../../common/constants';
import { TestIds } from '../../../assets/testIds';

interface StepRowProps {
  label: string;
  done: boolean;
  disabled?: boolean;
  onAction: () => void;
  onComplete: () => void;
  showDivider?: boolean;
}

function StepRow({
  label, done, disabled = false, onAction, onComplete, showDivider = true,
}: StepRowProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <>
      {done ? (
        <Box
          onClick={onAction}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.25),
            width: '100%',
            cursor: 'pointer',
          }}
        >
          <CheckIcon sx={{ color: theme.palette.grey[400], fontSize: theme.spacing(3) }} />
          <Typography variant="body2" sx={{ flex: 1, color: theme.palette.grey[400] }}>
            {label}
          </Typography>
          <OpenInNewIcon
            sx={{ color: theme.palette.grey[400], fontSize: theme.spacing(2.25) }}
          />
        </Box>
      ) : (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1),
          width: '100%',
        }}>
          <OptimusButton
            customVariant="primary"
            size="s"
            buttonText={label}
            trailingIcon={<OpenInNewIcon />}
            disabled={disabled}
            onClick={onAction}
          />
          <OptimusButton
            customVariant="secondaryGray"
            size="s"
            buttonText={
              t('homeDepot.enrollmentStatusPanel.markAsComplete') as string
            }
            leadingIcon={<TaskAltIcon />}
            disabled={disabled}
            onClick={onComplete}
          />
        </Box>
      )}
      {showDivider && (
        <Divider sx={{ width: '100%', borderColor: theme.palette.grey[200] }} />
      )}
    </>
  );
}

interface HDEnrollmentStatusPanelProps {
  status: Exclude<PartnerEnrollmentStatus, typeof PartnerEnrollmentStatus.NotStarted>;
  emailLoading?: boolean;
  partnerMemberEmail?: string;
  onEmailSubmit: (email: string) => void;
  onShopNow: () => void;
  showShopAction?: boolean;
}

export function HDEnrollmentStatusPanel({
  status,
  emailLoading = false,
  partnerMemberEmail,
  onEmailSubmit,
  onShopNow,
  showShopAction = true,
}: HDEnrollmentStatusPanelProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { openExternalLink } = useNavigationUtils();
  const { trackPartnerEnrollment } = useCustomEventTracking();
  const partnerConfigList = usePartnerConfigList();
  const partnerLogoUrl = partnerConfigList.find((p) => p.id === homeDepotPartnerId)?.partnerLogoUrl;

  const memberSession = useAppSelector(selectMemberSession);
  const memberId = memberSession?.memberId;

  const lsKeys = useMemo(() => ({
    step1: `hd_enrollment_step1_done_${memberId}`,
    step2: `hd_enrollment_step2_done_${memberId}`,
  }), [memberId]);

  const [createAccountDone, setCreateAccountDone] = useState(
    localStorage.getItem(lsKeys.step1) === 'true',
  );
  const [joistCodeDone, setJoistCodeDone] = useState(
    localStorage.getItem(lsKeys.step2) === 'true',
  );
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showVideoDialog, setShowVideoDialog] = useState(false);

  const videoUrl = createAccountDone ? HD_SIGN_IN_VIDEO_URL : HD_SIGN_UP_VIDEO_URL;

  const handleVideoPlay = () => {
    trackPartnerEnrollment('Play', 'HomeDepotOnboardingVideo', { videoUrl });
  };

  const handleVideoEnd = () => {
    trackPartnerEnrollment('Complete', 'HomeDepotOnboardingVideo', { videoUrl });
  };

  if (status === PartnerEnrollmentStatus.InProgress) {
    return (
      <Box
        data-testid="hd-enrollment-status-panel"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: theme.spacing(2),
          width: '100%',
          mt: theme.spacing(0.5),
        }}
      >
        <PartnerEnrollmentStatusBadge status={status} />
        <Typography
          variant="bodyMd"
          sx={{ width: '100%', color: theme.palette.grey[700] }}
        >
          {t('homeDepot.enrollmentStatusPanel.waitingForVerification') as string}
        </Typography>
      </Box>
    );
  }

  if (status === PartnerEnrollmentStatus.Completed) {
    const brandStyle = getPartnerShopButtonBrandStyle(THE_HOME_DEPOT_CODE);

    return (
      <Box
        data-testid="hd-enrollment-status-panel"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: theme.spacing(2),
          width: '100%',
          mt: theme.spacing(0.5),
        }}
      >
        <PartnerEnrollmentStatusBadge status={status} />
        {showShopAction && (
          <OptimusButton
            customVariant="primary"
            size="s"
            trailingIcon={<OpenInNewIcon />}
            buttonText={t('partnerDetails.enrollmentStatus.shopAtAndEarn', {
              partnerName: THE_HOME_DEPOT_NAME,
              defaultValue: `Shop at ${THE_HOME_DEPOT_NAME} and earn`,
            }) as string}
            onClick={onShopNow}
            sx={brandStyle ? {
              width: '100%',
              height: 'auto',
              backgroundColor: brandStyle.backgroundColor,
              color: brandStyle.color,
              fontWeight: 600,
              '&:hover': { backgroundColor: brandStyle.backgroundColor },
              '&:active': { backgroundColor: brandStyle.backgroundColor },
            } : { width: '100%', fontWeight: 600 }}
            data-testid={`${TestIds.partnerDetailsPage.shopNowBtnPrefix}-${THE_HOME_DEPOT_CODE}`}
          />
        )}
        {partnerMemberEmail && (
          <Typography
            variant="body2"
            sx={{ width: '100%', color: theme.palette.grey[700], wordBreak: 'break-all' }}
          >
            {`Account: ${partnerMemberEmail}`}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <>
      <HDEmailModal
        open={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSubmit={(email) => {
          setShowEmailModal(false);
          onEmailSubmit(email);
        }}
      />

      <Dialog
        open={showVideoDialog}
        onClose={() => setShowVideoDialog(false)}
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: theme.spacing(67.5),
            borderRadius: theme.spacing(1.5),
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            padding: theme.spacing(3),
            paddingRight: theme.spacing(6),
          }}
        >
          {partnerLogoUrl && (
            <Box
              sx={{
                width: theme.spacing(7.5),
                height: theme.spacing(7.5),
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: theme.spacing(1.25),
                flexShrink: 0,
                overflow: 'hidden',
                boxShadow: '0px 1px 2px 0px rgba(10,13,18,0.05)',
              }}
            >
              <Box
                component="img"
                src={partnerLogoUrl}
                alt="Home Depot Pro"
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
          )}
          <Typography variant="h4" color={theme.palette.grey[900]}>
            {t('homeDepot.enrollModal.title')}
          </Typography>
          <IconButton
            onClick={() => setShowVideoDialog(false)}
            size="small"
            sx={{ position: 'absolute', top: theme.spacing(2), right: theme.spacing(2) }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        {/* Video */}
        <Box
          sx={{
            background: theme.palette.common.black,
            margin: theme.spacing(3),
            borderRadius: theme.spacing(1.5),
            overflow: 'hidden',
          }}
        >
          <VideoPlayer
            videoUrl={videoUrl}
            handleOnPlay={handleVideoPlay}
            handleOnEnd={handleVideoEnd}
            optionalVideoContainerStyle={{
              borderRadius: theme.spacing(1.5),
              overflow: 'hidden',
            }}
          />
        </Box>

        {/* Close button */}
        <Box sx={{ padding: theme.spacing(3), paddingTop: 0 }}>
          <OptimusButton
            customVariant="primary"
            buttonText={t('button.close') as string}
            onClick={() => setShowVideoDialog(false)}
            sx={{ width: '100%' }}
          />
        </Box>
      </Dialog>

      <Box
        data-testid="hd-enrollment-status-panel"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: theme.spacing(2),
          width: '100%',
          mt: theme.spacing(0.5),
        }}
      >
        <PartnerEnrollmentStatusBadge status={status} />

        <StepRow
          label={t('homeDepot.enrollmentStatusPanel.step1') as string}
          done={createAccountDone}
          onAction={() => openExternalLink(HD_SITE_URL)}
          onComplete={() => {
            localStorage.setItem(lsKeys.step1, 'true');
            setCreateAccountDone(true);
          }}
        />
        <StepRow
          label={t('homeDepot.enrollmentStatusPanel.step2') as string}
          done={joistCodeDone}
          disabled={!createAccountDone}
          onAction={() => openExternalLink(HD_SITE_URL)}
          onComplete={() => {
            localStorage.setItem(lsKeys.step2, 'true');
            setJoistCodeDone(true);
          }}
        />

        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1),
          width: '100%',
        }}>
          {emailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: theme.spacing(0.5) }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <OptimusButton
              customVariant="primary"
              size="s"
              buttonText={t('homeDepot.enrollmentStatusPanel.step3') as string}
              disabled={!joistCodeDone}
              onClick={() => setShowEmailModal(true)}
            />
          )}
        </Box>

        <Box
          onClick={() => setShowVideoDialog(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.25),
            width: '100%',
            pt: theme.spacing(0.5),
            cursor: 'pointer',
          }}
        >
          <VideocamOutlinedIcon
            sx={{ color: theme.palette.brand.primary, fontSize: theme.spacing(3) }}
          />
          <Typography variant="body2" color={theme.palette.brand.primary}>
            {t('homeDepot.enrollmentStatusPanel.seeHowToVideo')}
          </Typography>
        </Box>
      </Box>
    </>
  );
}
